import { memo, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import qs from 'qs';
import constants from '../../../utils/constants';
import StatefulLink, { defaultFilter } from '../../stateful-link/StatefulLink';

import CheckboxFacet from './checkbox-facet/CheckboxFacet';
import BlockFacet from './block-facet/BlockFacet';
import DepartmentItem from './department-item/DepartmentItem';
import styles from './FacetItem.module.scss';

import { useFacetAnalyticsEvent } from '../facet-context';
import { getAriaTextIfPriceRange } from '../../../utils/index';
import { useFacetItemData } from './data/useFacetItemData';

const FacetItem = (props) => {
  const {
    style,
    value,
    dimensionName,
    showColourFilterSwatches,
    initialDisabled = false,
    mobile,
    isFilterDrawer = false,
    onFacetClicked = () => {},
    listItem = true,
    component,
    className,
    'data-ind': dataInd,
    hidden = false,
    last = false,
    divider = false,
  } = props;

  const { facetId, facetName = '', facetType, selected, label } = value;
  const { router, facetContext } = useFacetItemData();
  const { isMobile } = facetContext;

  const triggerAnalyticsEvent = useFacetAnalyticsEvent(props);

  const [disabled, setDisabled] = useState(initialDisabled);

  const scrollToTop = useCallback(() => {
    const body = document.getElementById('js-plp-body');
    if (body) {
      const viewportRelativeToGrid = body.getBoundingClientRect();
      const gridBelowCurrentViewport = viewportRelativeToGrid.top > 0;

      if (gridBelowCurrentViewport) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        body.scrollIntoView({ behavior: 'smooth' });
      }
    }
    router.events.off('routeChangeComplete', scrollToTop);
  }, []);

  const handleClick = useCallback(() => {
    setDisabled(true);

    if (!isMobile) {
      onFacetClicked();
    }
    triggerAnalyticsEvent();

    router.events.on('routeChangeComplete', scrollToTop);
  }, []);

  const showColourSwatch =
    showColourFilterSwatches &&
    (dimensionName === 'skus.basicColor' ||
      dimensionName === 'childSKUs.basicColor');

  useEffect(() => {
    if (disabled && selected) {
      setDisabled(false);
    }
  }, [disabled, selected]);

  let Component = CheckboxFacet;

  if (!mobile && dimensionName === constants.DEPARTMENT_DIMENSION_NAME) {
    Component = DepartmentItem;
  }

  if (showColourSwatch || mobile) {
    Component = BlockFacet;
  }

  if (component) {
    Component = component;
  }

  let queryParams = value.query;

  const queryOpts = { filter: defaultFilter() };

  if (typeof queryParams === 'string') {
    queryParams = qs.parse(queryParams);
    queryOpts.filter.push(...Object.keys(queryParams));
  }

  const ariaLabel = getAriaTextIfPriceRange(label);

  const sanitiseString = (string) => string.replace(/\s/g, '-');
  const facetNameLabel = facetName ? sanitiseString(facetName) + '-' : '';
  const labelId = facetNameLabel + sanitiseString(label);
  const drawerLabel = isFilterDrawer ? 'drawer-' : '';

  const link = (
    <StatefulLink
      facetId={facetId}
      facetType={facetType}
      query={{
        ...queryParams,
        page: undefined,
      }}
      queryOpts={queryOpts}
      className={classnames(className || styles.link, {
        [styles.active]: selected,
        [styles.disabled]: disabled,
      })}
      onClick={handleClick}
      aria-checked={selected}
      disabled={disabled}
      data-testid={label}
      role="checkbox"
      data-ind={dataInd}
      scroll={false}
    >
      <Component
        id={drawerLabel + labelId}
        name={labelId}
        value={value}
        ariaLabel={ariaLabel}
        dimensionName={dimensionName}
        disabled={disabled}
        mobile
        showColourSwatch={showColourSwatch}
      />
    </StatefulLink>
  );

  return listItem ? (
    <li
      style={style}
      className={classnames(styles.listItem, {
        [styles.nopadding]: mobile,
        [styles.bordered]: showColourSwatch,
        [styles.hidden]: hidden,
        [styles.last]: last,
        [styles.divider]: divider,
      })}
      visible={hidden ? 'false' : 'true'}
    >
      {link}
    </li>
  ) : (
    link
  );
};

export default memo(FacetItem);
